// @ts-check
/* eslint-env browser */

export class PdfViewer extends HTMLElement {
  #minWidth = 318
  #maxWidth = 0
  #currentWidth = 0
  #isDragging = false
  #startX = 0
  #startWidth = 0
  /** @type {HTMLDivElement | null} */
  #resizeHandle = null
  #iframe = null
  /** @type {HTMLElement | null} */
  #announcementRegion = null

  connectedCallback () {
    this.#render()
    this.#setupElements()
    this.#calculateInitialWidth()
    this.#setupDragHandlers()
    this.#setupKeyboardHandlers()
  }

  #render () {
    const src = this.getAttribute('src') || ' '
    this.innerHTML = `
      <div class="pdf-viewer__container" role="region" aria-label="PDF viewer">
        <div 
          class="pdf-viewer__resize-handle"
          aria-valuemin="${this.#minWidth}"
          aria-valuemax="0"
          aria-valuenow="${this.#currentWidth}"
          aria-orientation="vertical"
          tabindex="0"
          role="separator"
        >
          <svg aria-hidden="true" focusable="false" height="24px" viewBox="0 -960 960 960" width="24px"><path d="M480-80 240-320l57-57 183 183 183-183 57 57L480-80ZM298-584l-58-56 240-240 240 240-58 56-182-182-182 182Z"/></svg>
          <span class="govuk-visually-hidden">Use left and right arrow keys to resize the PDF viewer</span>
        </div>
        <iframe 
          class="pdf-viewer__iframe" 
          title="PDF of the source document" 
          src="${src}"
        ></iframe>
        <div class="pdf-viewer__drag-overlay"></div>
        <div class="pdf-viewer__announcement" aria-live="polite" aria-atomic="true"></div>
      </div>
    `
  }

  #setupElements () {
    this.#resizeHandle = /** @type {HTMLDivElement | null} */ (this.querySelector('.pdf-viewer__resize-handle'))
    // @ts-ignore - querySelector returns Element | null, but we know it's an iframe
    this.#iframe = this.querySelector('.pdf-viewer__iframe')
    this.#announcementRegion = /** @type {HTMLElement | null} */ (this.querySelector('.pdf-viewer__announcement'))
  }

  #calculateInitialWidth () {
    this.#currentWidth = this.scrollWidth
    this.style.setProperty('--pdf-viewer-width', `${this.#currentWidth}px`)
    this.#maxWidth = (this.closest('.map-container')?.scrollWidth || 0) - 500
  }

  #setupDragHandlers () {
    if (!this.#resizeHandle) return

    this.#resizeHandle.addEventListener('mousedown', (e) => {
      e.preventDefault()
      this.#startDrag(e.clientX)
    })

    document.addEventListener('mousemove', (e) => {
      if (this.#isDragging) {
        this.#handleDrag(e.clientX)
      }
    })

    document.addEventListener('mouseup', () => {
      if (this.#isDragging) {
        this.#endDrag()
      }
    })
  }

  #setupKeyboardHandlers () {
    if (!this.#resizeHandle) return

    this.#resizeHandle.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        const step = 10 // pixels per arrow key press
        const newWidth = e.key === 'ArrowLeft'
          ? Math.min(this.#maxWidth, this.#currentWidth + step)
          : Math.max(this.#minWidth, this.#currentWidth - step)

        this.#setWidth(newWidth)
        this.#announceWidth(newWidth)
      }
    })
  }

  #startDrag (clientX) {
    this.#isDragging = true
    this.#startX = clientX
    this.#startWidth = this.#currentWidth
    document.body.classList.add('pdf-viewer--dragging')
  }

  #handleDrag (clientX) {
    const deltaX = this.#startX - clientX // Inverted because we're dragging from left
    const newWidth = Math.max(this.#minWidth, Math.min(this.#maxWidth, this.#startWidth + deltaX))
    this.#setWidth(newWidth)
  }

  #endDrag () {
    this.#isDragging = false
    document.body.classList.remove('pdf-viewer--dragging')
    this.#announceWidth(this.#currentWidth)
  }

  #setWidth (width) {
    this.#currentWidth = width
    this.style.setProperty('--pdf-viewer-width', `${width}px`)
    if (this.#resizeHandle) {
      this.#resizeHandle.setAttribute('aria-valuenow', width.toString())
    }
  }

  #announceWidth (width) {
    if (this.#announcementRegion) {
      this.#announcementRegion.textContent = `PDF viewer width: ${Math.round(width)} pixels`
      // Clear after announcement
      setTimeout(() => {
        if (this.#announcementRegion) {
          this.#announcementRegion.textContent = ''
        }
      }, 1000)
    }
  }
}

customElements.define('pdf-viewer', PdfViewer)
